import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import { createPortal } from "react-dom";

import { Input } from "@runcheetah/ui";

const HEX_COLOR_RE = /^#[0-9a-f]{6}$/i;

type HsvColor = {
  h: number;
  s: number;
  v: number;
};

type CreativeColorPickerProps = {
  value: string;
  brandColors?: string[];
  brandPaletteLabel: string;
  hexInputLabel: string;
  hueSliderLabel: string;
  openPickerLabel: string;
  useBrandColorLabel: (color: string) => string;
  onCommit: (value: string) => void;
};

type PopoverPosition = {
  left: number;
  top: number;
  width: number;
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const normalizeHexColor = (value: string) => {
  const trimmed = value.trim();
  return HEX_COLOR_RE.test(trimmed) ? trimmed.toUpperCase() : "#FFFFFF";
};

const hexToHsv = (hex: string): HsvColor => {
  const normalized = normalizeHexColor(hex).slice(1);
  const r = Number.parseInt(normalized.slice(0, 2), 16) / 255;
  const g = Number.parseInt(normalized.slice(2, 4), 16) / 255;
  const b = Number.parseInt(normalized.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;

  if (delta !== 0) {
    if (max === r) {
      h = 60 * (((g - b) / delta) % 6);
    } else if (max === g) {
      h = 60 * ((b - r) / delta + 2);
    } else {
      h = 60 * ((r - g) / delta + 4);
    }
  }

  return {
    h: h < 0 ? h + 360 : h,
    s: max === 0 ? 0 : delta / max,
    v: max,
  };
};

const hsvToHex = ({ h, s, v }: HsvColor) => {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }

  return [r, g, b]
    .map((channel) =>
      Math.round((channel + m) * 255)
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")
    .toUpperCase()
    .padStart(6, "0")
    .replace(/^/, "#");
};

export function CreativeColorPicker({
  value,
  brandColors = [],
  brandPaletteLabel,
  hexInputLabel,
  hueSliderLabel,
  openPickerLabel,
  useBrandColorLabel,
  onCommit,
}: CreativeColorPickerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const colorPlaneRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState<PopoverPosition | null>(null);
  const normalizedValue = normalizeHexColor(value);
  const [hexDraft, setHexDraft] = useState(normalizedValue);
  const hsv = useMemo(() => hexToHsv(normalizedValue), [normalizedValue]);
  const currentColor = normalizedValue.toLowerCase();

  useEffect(() => {
    setHexDraft(normalizedValue);
  }, [normalizedValue]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: globalThis.PointerEvent) => {
      const target = event.target as Node;

      if (!containerRef.current?.contains(target) && !popoverRef.current?.contains(target)) {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) {
      return;
    }

    const updatePopoverPosition = () => {
      const triggerRect = triggerRef.current?.getBoundingClientRect();
      if (!triggerRect) {
        return;
      }

      const width = Math.max(triggerRect.width, 238);
      const left = clamp(triggerRect.left, 8, window.innerWidth - width - 8);
      let top = triggerRect.bottom + 8;
      const popoverHeight = popoverRef.current?.getBoundingClientRect().height ?? 0;

      if (popoverHeight && top + popoverHeight > window.innerHeight - 8) {
        top = Math.max(8, triggerRect.top - popoverHeight - 8);
      }

      setPopoverPosition({ left, top, width });
    };

    updatePopoverPosition();
    const animationFrame = requestAnimationFrame(updatePopoverPosition);
    window.addEventListener("resize", updatePopoverPosition);
    window.addEventListener("scroll", updatePopoverPosition, true);

    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", updatePopoverPosition);
      window.removeEventListener("scroll", updatePopoverPosition, true);
    };
  }, [open]);

  const commitHsv = (nextHsv: HsvColor) => onCommit(hsvToHex(nextHsv));

  const updateFromColorPlane = (event: PointerEvent<HTMLDivElement>) => {
    const rect = colorPlaneRef.current?.getBoundingClientRect();
    if (!rect) {
      return;
    }

    commitHsv({
      h: hsv.h,
      s: clamp((event.clientX - rect.left) / rect.width, 0, 1),
      v: 1 - clamp((event.clientY - rect.top) / rect.height, 0, 1),
    });
  };

  const commitHexDraft = () => {
    if (HEX_COLOR_RE.test(hexDraft)) {
      onCommit(hexDraft.toUpperCase());
    } else {
      setHexDraft(normalizedValue);
    }
  };

  return (
    <div ref={containerRef} className="relative min-w-0">
      <button
        ref={triggerRef}
        type="button"
        className="flex min-w-0 w-full items-center gap-2 rounded-2xl border border-border/65 bg-[#f7f4eb] px-3 py-2 text-left shadow-[inset_0_1px_0_rgba(255,255,255,0.8)] transition-colors hover:border-foreground/18 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        aria-label={openPickerLabel}
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span
          className="block h-7 w-7 shrink-0 rounded-xl border border-black/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.45),0_4px_10px_rgba(19,34,49,0.08)]"
          style={{ background: normalizedValue }}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1 truncate font-mono text-xs font-semibold">
          {normalizedValue}
        </span>
      </button>

      {open
        ? createPortal(
            <div
              ref={popoverRef}
              className="fixed z-[80] min-w-[238px] rounded-3xl border border-border/70 bg-[#fffdf8] p-3 shadow-[0_20px_60px_rgba(19,34,49,0.18)]"
              style={{
                left: popoverPosition?.left ?? 8,
                top: popoverPosition?.top ?? 8,
                width: popoverPosition?.width ?? 238,
              }}
            >
              <div
                ref={colorPlaneRef}
                className="relative h-36 cursor-crosshair overflow-hidden rounded-2xl border border-black/10 touch-none shadow-inner"
                style={{
                  background: `linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, hsl(${hsv.h} 100% 50%))`,
                }}
                onPointerDown={(event) => {
                  event.currentTarget.setPointerCapture(event.pointerId);
                  updateFromColorPlane(event);
                }}
                onPointerMove={(event) => {
                  if (event.buttons === 1) {
                    updateFromColorPlane(event);
                  }
                }}
              >
                <span
                  className="absolute h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow-[0_0_0_1px_rgba(0,0,0,0.35),0_2px_8px_rgba(0,0,0,0.25)]"
                  style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%` }}
                  aria-hidden="true"
                />
              </div>

              <input
                type="range"
                min={0}
                max={359}
                value={Math.round(hsv.h)}
                aria-label={hueSliderLabel}
                onChange={(event) => commitHsv({ ...hsv, h: Number(event.target.value) })}
                className="mt-3 h-3 w-full cursor-pointer appearance-none rounded-full bg-[linear-gradient(to_right,#f00,#ff0,#0f0,#0ff,#00f,#f0f,#f00)] accent-foreground"
              />

              <Input
                value={hexDraft}
                aria-label={hexInputLabel}
                onChange={(event) => setHexDraft(event.target.value)}
                onBlur={commitHexDraft}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    commitHexDraft();
                  }
                }}
                className="mt-3 h-9 rounded-2xl bg-background font-mono text-xs font-semibold"
              />

              {brandColors.length ? (
                <div className="mt-3 space-y-1.5 border-t border-border/55 pt-3">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-foreground/45">
                    {brandPaletteLabel}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {brandColors.map((color) => {
                      const isActive = color.toLowerCase() === currentColor;

                      return (
                        <button
                          key={color}
                          type="button"
                          className={[
                            "h-7 w-7 rounded-lg border border-black/10 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.4)] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50",
                            isActive ? "ring-2 ring-foreground/40 ring-offset-2" : "",
                          ].join(" ")}
                          style={{ backgroundColor: color }}
                          title={color}
                          aria-label={useBrandColorLabel(color)}
                          aria-pressed={isActive}
                          onClick={() => onCommit(color)}
                        />
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
